import React, { FC } from 'react';
import { cx } from '@emotion/css';
import { useTranslation } from 'next-i18next';

import { formatDateStr } from '@phoenix/core/frontend/shared/util/format/dates';
import { ProductDetails, ProductListItem } from '@phoenix/team-m/my-tool-shed/frontend/util/product/product.model';

import { WarrantyEndDate } from '../warranty-end-date';
import { LabelValue } from '../label-value';

interface ToolInfoWithLifecycleProps {
  product?: ProductListItem | ProductDetails;
  isLoading?: boolean;
  showSerialAndArticleNumbers?: boolean;
  customContainerClasses?: string;
}

export const ToolInfoWithLifecycle: FC<React.PropsWithChildren<ToolInfoWithLifecycleProps>> = ({
  product = {} as ProductDetails,
  isLoading = false,
  showSerialAndArticleNumbers = false,
  customContainerClasses = '',
}) => {
  const { t } = useTranslation();
  const nextServiceDate = product?.serviceReminder?.nextServiceDate || undefined;

  return (
    <dl className={cx(!isLoading && 'grid-cols-[minmax(max-content,1fr),1fr]', 'grid gap-x-4 gap-y-2', customContainerClasses)}>
      {showSerialAndArticleNumbers && (
        <>
          {(isLoading || product.serialNumber) && (
            <LabelValue
              label={t('toolShed_overview_listItem_label_serialNumber')}
              value={product['serialNumber']}
              dataTestId="serialNumber"
              isLoading={isLoading}
            />
          )}
          {(isLoading || product['articleNumber']) && (
            <LabelValue
              label={t('toolShed_overview_listItem_label_articleNumber')}
              value={product['articleNumber']}
              dataTestId="articleNumber"
              isLoading={isLoading}
            />
          )}
        </>
      )}
      <LabelValue
        label={t('toolShed_overview_listItem_label_purchaseDate')}
        value={product.purchaseDate && formatDateStr(product.purchaseDate)}
        dataTestId="purchaseDate"
        isLoading={isLoading}
        isAlternativeBrand={!product.serialNumber}
      />
      <LabelValue
        label={t('toolShed_overview_listItem_label_registrationDate')}
        value={product.registrationDate && formatDateStr(product.registrationDate)}
        dataTestId="registrationDate"
        isLoading={isLoading}
      />
      <WarrantyEndDate product={product} isLoading={isLoading} />
      {(isLoading || product.serialNumber) && (
        <LabelValue
          label={t('toolShed_overview_listItem_label_nextService')}
          value={nextServiceDate && formatDateStr(nextServiceDate)}
          dataTestId="nextServiceDate"
          isLoading={isLoading}
        />
      )}
    </dl>
  );
};
